/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosCard,
	LicenseCardButtons,
	LicenseCardCore,
	LicenseCardSeats,
	LicenseCardTop,
} from "@components/elements";
import { cva, cx, RecipeVariantProps } from "@panda/css";
import { VStack } from "@panda/jsx";

import {
	ChangeEvent,
	ChangeEventHandler,
	FocusEvent,
	FocusEventHandler,
	MouseEventHandler,
	useCallback,
	useState,
} from "react";
import { CosmosEnumCountryCodes, LicenseMainCardFragment } from "@cosmos/hygraph-schema";
import { usePrice } from "@cosmos/next-hooks";
import { DEFAULT_UE_SEAT_PRICE_NUM, DEFAULT_UE_SEAT_PRICE_STR } from "@components/constants";

export interface LicenseSeatData {
	defaultPriceNum?: number;
	defaultPriceStr?: string;
	seatOfferId?: string;
	seatNamespace?: string;
	seatPurchaseUrl?: string; // This should be the seat purchase url with params expecting seatCount to be appended, like `...quanity=${seatCount}
}

type LicenseMainCardWithSeatData = LicenseMainCardFragment & LicenseSeatData;

const cardStyleCva = cva({
	variants: {
		cardAmount: {
			single: {
				flexBasis: "100%",
				padding: "eds.dimension.0",
				"& .license-main-content": {
					gap: "0",
					alignItems: "stretch",
				},
				"& .license-main-content-top": {
					borderBottomWidth: "1px",
					borderBottomStyle: "solid",
					borderBottomColor: "eds.border.subdued",
					padding: "eds.dimension.32",
				},
				"& .license-main-content-bottom": {
					gap: "0",
					alignItems: "stretch",
					md: {
						flexDirection: "row",
						"& > div": {
							flexBasis: "50%",
						},
					},
					mdDown: {
						flexDirection: "column",
					},
				},
				"& .license-main-content-core": {
					md: {
						borderInlineEndWidth: "1px",
						borderInlineEndStyle: "solid",
						borderInlineEndColor: "eds.border.subdued",
					},
					mdDown: {
						borderBottomWidth: "1px",
						borderBottomStyle: "solid",
						borderBottomColor: "eds.border.subdued",
					},
					padding: "eds.dimension.32",
				},
				"& .license-main-content-seats": {
					padding: "eds.dimension.32",
				},
			},
			default: {
				flexBasis: "50%",
				padding: "eds.dimension.32",
			},
		},
	},
	defaultVariants: {
		cardAmount: "default",
	},
});
type CardRecipeVariants = Exclude<RecipeVariantProps<typeof cardStyleCva>, undefined>;
export interface LicenseMainCardProps extends LicenseMainCardWithSeatData {
	locale: string;
	country: CosmosEnumCountryCodes;
	className?: string;
	showExampleLabel?: string | null;
	hideExampleLabel?: string | null;
	cardAmount?: CardRecipeVariants["cardAmount"];
}

export const LicenseMainCard = ({
	locale,
	country,
	className = "",
	cardTop,
	cardSections = [],
	cardAmount,
	seats,
	showExampleLabel,
	hideExampleLabel,
	defaultPriceNum,
	defaultPriceStr,
	seatOfferId,
	seatNamespace,
	seatPurchaseUrl,
}: LicenseMainCardProps): React.JSX.Element => {
	const { price } = usePrice(locale, seatOfferId || "", seatNamespace || "", country);
	const divideBy = Math.pow(10, price?.currencyInfo?.decimals || 0);
	const priceNum = (price?.discountPrice || defaultPriceNum || DEFAULT_UE_SEAT_PRICE_NUM) / divideBy;
	const priceStr = (price?.discountPriceStr || defaultPriceStr || DEFAULT_UE_SEAT_PRICE_STR).replace(/\.00$/, "");

	const [seatCount, setSeatCount] = useState(1);
	const handleDecrementClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
		setSeatCount((prev) => Math.max(prev - 1, 1));
	}, []);

	const handleIncrementClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
		setSeatCount((prev) => Math.min(prev + 1, 25));
	}, []);

	const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const inputValue = parseInt(e?.target?.value, 10);
		if (!isNaN(inputValue)) setSeatCount(Math.min(Math.max(inputValue, 1), 25));
	}, []);

	const handleInputBlur: FocusEventHandler<HTMLInputElement> = useCallback((e: FocusEvent<HTMLInputElement>) => {
		const inputValue = parseInt(e?.target?.value, 10);
		if (isNaN(inputValue)) setSeatCount(1);
	}, []);

	const isCountryOverridden = !!seats?.countriesToOverride?.includes(country);

	return (
		<CosmosCard
			className={cx("license-main-card", className, cardStyleCva({ cardAmount }))}
			variant="material"
			padding={0}
		>
			<VStack alignItems="stretch" gap="eds.dimension.32" className="license-main-content" h="full">
				{cardTop ? (
					<VStack alignItems="flex-start" gap="eds.dimension.32" className="license-main-content-top">
						<LicenseCardTop
							{...(cardTop || {})}
							showExampleLabel={showExampleLabel}
							hideExampleLabel={hideExampleLabel}
						/>
					</VStack>
				) : null}
				<VStack
					alignItems="flex-start"
					gap="eds.dimension.32"
					justifyContent="space-between"
					flexGrow="1"
					className="license-main-content-bottom"
				>
					<VStack alignItems="flex-start" gap="eds.dimension.32" className="license-main-content-core">
						{(cardSections || []).map((section) => (
							<LicenseCardCore
								key={`card-section-${section.title}`}
								priceStr={priceStr}
								{...(section?.countriesToOverride?.includes(country)
									? { ...section, ...section?.countryOverride }
									: section)}
							/>
						))}
					</VStack>
					<VStack alignItems="flex-start" gap="eds.dimension.32" className="license-main-content-seats">
						{seats && !isCountryOverridden ? (
							<LicenseCardSeats
								locale={locale}
								{...seats}
								buttons={seats?.buttons || []}
								countriesToOverride={seats?.countriesToOverride || []}
								seatCount={seatCount}
								country={country}
								currencyCode={price?.currencyCode}
								priceNum={priceNum}
								priceStr={priceStr}
								onDecrementClick={handleDecrementClick}
								onIncrementClick={handleIncrementClick}
								onInputChange={handleInputChange}
								onInputBlur={handleInputBlur}
							/>
						) : null}
						{seats ? (
							<LicenseCardButtons
								buttons={isCountryOverridden ? seats?.countryOverride?.buttons || [] : seats?.buttons || []}
								seatCount={isCountryOverridden ? 0 : seatCount}
								seatPurchaseUrl={seatPurchaseUrl}
							/>
						) : (
							<LicenseCardButtons buttons={cardSections[cardSections.length - 1]?.buttons || []} />
						)}
					</VStack>
				</VStack>
			</VStack>
		</CosmosCard>
	);
};
